/**
 * Мост между WebSocket-событиями и RTK Query кэшем.
 *
 * Подписываемся один раз на realtime-клиент, мапим события на
 * `osnovaApi.util.invalidateTags(...)` или точечные `updateQueryData`.
 * RTK Query сам подхватывает инвалидацию и рефетчит только активные
 * подписки (компоненты, реально показанные на экране).
 */

import { osnovaApi, statutApi } from '@shared/api';
import {
  progressReceived,
  progressCleared,
  protocolGenerationFinished,
} from '@features/analysisProgress/model/slice';
import { realtime, type AgentEvent } from './ws';

// Принимаем любой Redux dispatch — RTK Query возвращает thunks, которые
// нативный redux Dispatch<UnknownAction> формально не принимает. Расширяем
// тип так, чтобы он принимал ЛЮБОЕ действие (включая функции-thunks).
// eslint-disable-next-line @typescript-eslint/no-explicit-any
type AnyDispatch = (action: any) => any;

/**
 * События агента ОСНОВА.Экономист. Объявлены здесь, а не в `ws.ts`,
 * потому что инфраструктурный glue-PR не трогает базовый union
 * `AgentEvent` (его расширяет PR со страницами экономиста). Подписчик
 * принимает супертип `AgentEvent | EconBridgeEvent` — за счёт
 * контравариантности параметров такой обработчик корректно передаётся
 * туда, где ждут `Handler`, принимающий `AgentEvent`. Backend-варианты
 * см. `osnova_core::events::AgentEvent` (`econ_run_processed`,
 * `econ_report_composed`).
 */
type EconBridgeEvent =
  | { event: 'econ_run_processed'; at: string; run_id: string }
  | { event: 'econ_report_composed'; at: string; run_id: string }
  // Живой статус/прогресс прогона — backend публикует на каждом изменении
  // (см. контракт `econ_run_updated`). `status`/`progress` в payload не
  // используем для инвалидации (теги не зависят от их значения) — RTK сам
  // перетянет свежую карточку прогона; поля типизируем для полноты контракта.
  | {
      event: 'econ_run_updated';
      at: string;
      run_id: string;
      status: 'processing' | 'completed' | 'failed';
      progress: number;
    }
  // Сбор и переходы периода БДР v2 (`econ_bdr_updated`): экран перетянет
  // сетку и открытую версию.
  | { event: 'econ_bdr_updated'; at: string; period_id: string; status: string };

export function installRealtimeBridge(dispatch: AnyDispatch): () => void {
  const seenKspEvents = new Set<string>();
  return realtime.subscribe((event: AgentEvent | EconBridgeEvent) => {
    switch (event.event) {
      case 'connected':
        // On reconnect re-read KSP state instead of reconstructing lost sums.
        dispatch(osnovaApi.util.invalidateTags(['KspProject', 'KspSnapshot', 'ProgressObject']));
        break;

      case 'ksp.inputs_changed':
      case 'ksp.calculation_completed':
      case 'ksp.snapshot_published':
      case 'ksp.period_plan_released':
      case 'ksp.milestone_alert_opened':
      case 'ksp.milestone_alert_closed':
      case 'ksp.delivery_failed':
        if (seenKspEvents.has(event.event_id)) break;
        seenKspEvents.add(event.event_id);
        if (seenKspEvents.size > 1000) seenKspEvents.delete(seenKspEvents.values().next().value!);
        dispatch(osnovaApi.util.invalidateTags([{ type: 'KspProject', id: event.project_id }, { type: 'KspSnapshot', id: event.project_id }, 'ProgressObject', 'Progress']));
        break;

      case 'contract_analyzed':
      case 'contract_patched':
        // Карточка договора и список — у обоих один тег.
        dispatch(
          osnovaApi.util.invalidateTags([{ type: 'Contract', id: event.contract_id }, 'Contract']),
        );
        // После завершения анализа — чистим прогресс. contract_patched шлёт и
        // фоновая задача, когда разбор/анализ закончился неудачей: без сброса
        // локальный прогресс держал бы «идёт перепроверка» до перезагрузки.
        // Если анализ на самом деле идёт, следующее contract_analysis_progress
        // вернёт полосу, а статус under_review держит её и без локальной записи.
        dispatch(progressCleared(event.contract_id));
        break;

      case 'contract_under_review':
        dispatch(
          osnovaApi.util.invalidateTags([{ type: 'Contract', id: event.contract_id }, 'Contract']),
        );
        break;

      case 'contract_analysis_progress':
        // Реал-тайм прогресс анализа — сохраняем в Redux,
        // ContractCard читает и показывает стадии + найденные риски.
        dispatch(
          progressReceived({
            contract_id: event.contract_id,
            version_id: event.version_id,
            stage: event.stage,
            window_current: event.window_current,
            window_total: event.window_total,
            risks_so_far: event.risks_so_far,
          }),
        );
        break;

      case 'contract_deleted':
        // Из списка убираем точечно (без рефетча), карточку чистим.
        dispatch(
          statutApi.util.updateQueryData('listContracts', undefined, (draft) => {
            draft.items = draft.items.filter((c) => c.id !== event.contract_id);
          }),
        );
        dispatch(
          osnovaApi.util.invalidateTags([
            { type: 'Contract', id: event.contract_id },
            // Сроки этого договора удалены в БД — рефрешим реестр сроков.
            'Deadline',
            { type: 'Deadline', id: `for-contract-${event.contract_id}` },
          ]),
        );
        dispatch(progressCleared(event.contract_id));
        break;

      case 'risk_annotated':
        // Карточка договора подтянет свежий список рисков.
        dispatch(osnovaApi.util.invalidateTags([{ type: 'Contract', id: event.contract_id }]));
        break;

      case 'deadline_created':
      case 'deadline_notified':
      case 'deadline_overdue':
        dispatch(
          osnovaApi.util.invalidateTags([
            'Deadline',
            {
              type: 'Deadline',
              id: `for-${event.scope}-${event.entity_id}`,
            },
            // Карточка сущности (договор/претензия/письмо) тоже подтянет
            // обновлённый список deadlines.
            { type: 'Contract', id: event.entity_id },
            { type: 'Claim', id: event.entity_id },
            { type: 'Correspondence', id: event.entity_id },
          ]),
        );
        break;

      case 'correspondence_received':
      case 'correspondence_classified':
        dispatch(
          osnovaApi.util.invalidateTags([
            'Correspondence',
            { type: 'Correspondence', id: event.correspondence_id },
          ]),
        );
        break;

      case 'counterparty_refreshed':
        dispatch(
          osnovaApi.util.invalidateTags([
            { type: 'Counterparty', id: event.counterparty_id },
            'Counterparty',
          ]),
        );
        break;

      case 'claim_under_review':
      case 'claim_analysis_progress':
      case 'claim_analyzed':
        // Карточка претензии и реестр читают теги Claim / Claim:id.
        // Прогрессы (claim_analysis_progress) тоже инвалидируют — карточка
        // подтянет обновлённый `analysis_status` и risks из бэка. Это проще,
        // чем отдельный slice, и не теряет состояние при reconnect.
        dispatch(
          osnovaApi.util.invalidateTags([{ type: 'Claim', id: event.claim_id }, 'Claim']),
        );
        break;

      case 'claim_response_composed':
        // Шаблонизатор (ADR-0001 Stage D) собрал DOCX-черновик. Инвалидируем
        // три уровня тегов сразу:
        //  - { ComposedResponses, claim_id } — список в карточке претензии;
        //  - 'ComposedResponses' (generic) — страница «Архив документов»,
        //    она подписана на generic тег, без неё юрист видел архив пустым
        //    пока сам не обновит вкладку (баг #54 на проде 2026-05-26);
        //  - { Claim, claim_id } для обновления превью карточки.
        dispatch(
          osnovaApi.util.invalidateTags([
            { type: 'ComposedResponses', id: event.claim_id },
            'ComposedResponses',
          ]),
        );
        break;

      case 'contract_protocol_composed':
        // Stage G: DOCX-протокол разногласий по договору готов.
        // Инвалидируем и list_for_contract в ContractCard, и generic тег
        // для страницы «Архив документов» — иначе архив остаётся стейл
        // до явного F5 (см. длинный комментарий выше для claim_response_composed).
        dispatch(
          osnovaApi.util.invalidateTags([
            { type: 'ComposedResponses', id: `contract:${event.contract_id}` },
            'ComposedResponses',
          ]),
        );
        dispatch(protocolGenerationFinished(event.contract_id));
        break;

      case 'composed_deleted':
        // Юрист удалил один из сгенерированных DOCX. Backend заполнил
        // claim_id ИЛИ contract_id (CHECK constraint миграции 0049
        // гарантирует ровно один). Инвалидируем оба возможных tag-варианта
        // (карточки) и generic тег (страница Архив документов).
        dispatch(
          osnovaApi.util.invalidateTags([
            ...(event.claim_id
              ? [{ type: 'ComposedResponses' as const, id: event.claim_id }]
              : []),
            ...(event.contract_id
              ? [
                  {
                    type: 'ComposedResponses' as const,
                    id: `contract:${event.contract_id}`,
                  },
                ]
              : []),
            'ComposedResponses',
            // Composed мог быть привязан и к иску (миграция 0051 расширила
            // CHECK на 3 ветки claim_id ⊕ contract_id ⊕ lawsuit_id). Backend
            // не отдаёт lawsuit_id в payload, поэтому страхуемся generic-тегом
            // 'Lawsuit' — карточка иска перетянет свой composed-список.
            'Lawsuit',
          ]),
        );
        break;

      case 'claim_deleted':
        // Точечно убираем из активного списка без рефетча (всё равно сразу
        // оптимистично удалено локально, это страховка для других вкладок).
        // Реестр сроков подтягиваем — связанные дедлайны удалены каскадно.
        // Иски с claim_id == event.claim_id — backend сбросил FK в NULL
        // (ON DELETE SET NULL), карточка иска должна перерисоваться с
        // подписью «исходная претензия удалена». Generic-тег 'Lawsuit'
        // дешевле, чем тащить лист связанных исков с бэка.
        dispatch(
          osnovaApi.util.invalidateTags([
            { type: 'Claim', id: event.claim_id },
            'Claim',
            'Deadline',
            'Lawsuit',
          ]),
        );
        break;

      case 'lawsuit_composed':
        // Worker ADR-0001 завершил сборку DOCX иска. Инвалидируем:
        //  - { Lawsuit, id } — карточка LawsuitCard перерисуется со
        //    status='ready' и подтянет composed[0].id для кнопки «Скачать»;
        //  - 'Lawsuit' (generic) — список LawsuitsTab покажет статус;
        //  - { ComposedResponses, id: 'lawsuit:<id>' } — composed-блок в
        //    карточке (getLawsuit отдаёт composed одним запросом, но тег
        //    отдельный — на случай если бэк перейдёт на раздельный
        //    list_composed_for_lawsuit endpoint);
        //  - 'ComposedResponses' (generic) — страница «Архив документов».
        dispatch(
          osnovaApi.util.invalidateTags([
            { type: 'Lawsuit', id: event.lawsuit_id },
            'Lawsuit',
            { type: 'ComposedResponses', id: `lawsuit:${event.lawsuit_id}` },
            'ComposedResponses',
          ]),
        );
        break;

      case 'lawsuit_failed':
        // Сборка иска упала (LLM-таймаут, doc-processor не распарсил
        // ручной DOCX, и т.п.). Backend записал error_message и статус
        // 'failed' — карточка покажет красную плашку и кнопку
        // «Перегенерировать».
        dispatch(
          osnovaApi.util.invalidateTags([
            { type: 'Lawsuit', id: event.lawsuit_id },
            'Lawsuit',
          ]),
        );
        break;

      case 'claim_risk_annotated':
        // Другой юрист разметил риск — подтянем свежие данные карточки.
        dispatch(
          osnovaApi.util.invalidateTags([{ type: 'Claim', id: event.claim_id }]),
        );
        break;

      case 'audit_session_updated':
        // Worker завершил/прогрессирует парсинг внешнего аудита.
        // Инвалидируем список сессий конкретной entity — модалка перетянет.
        dispatch(
          osnovaApi.util.invalidateTags([
            { type: 'AuditSession', id: `${event.scope}:${event.entity_id}` },
          ]),
        );
        break;

      case 'entity_counterparties_extracted':
        // LLM выделил упомянутые ИНН в договоре/претензии — блок
        // «Упомянутые стороны» в карточке инвалидируется и подтягивает
        // свежий список одним запросом.
        dispatch(
          osnovaApi.util.invalidateTags([
            { type: 'Parties', id: `${event.entity_id}:${event.entity_type}` },
          ]),
        );
        break;

      case 'kadry_notification':
        // Кадры: ответ кандидата / новые кандидаты / тревога — список
        // уведомлений и реестры заявок перечитываются сразу (кадровик видит
        // «Подходит» без обновления страницы).
        dispatch(
          osnovaApi.util.invalidateTags(['KadryNotification', 'KadryRegistry', 'KadryOverview']),
        );
        break;

      case 'announcement_published':
        // Опубликовано новое обновление «Что нового» — инвалидируем ленту.
        // Колокольчик перетянет свежий список и пересчитает бейдж, а как только
        // рефетч долетит, `UpdatesPopup` сам наполнит очередь показа из ленты
        // (его useEffect по `announcements`) и всплывёт центрированной модалкой.
        //
        // НЕ докидываем id в очередь напрямую отсюда: раньше так и делали, но
        // это порождало гонку. WS кладёт id в очередь СИНХРОННО, до того как
        // рефетч ленты долетит, — карточки ещё нет в `listAnnouncements`,
        // `UpdatesPopup` не может её резолвить, и «мёртвый id»-эффект в окне
        // `!isFetching` выкидывал свежий id из очереди (оставляя его в
        // `enqueuedIds` навсегда). Пришедшая следом лента его уже не
        // возвращала — попап не всплывал, только колокольчик обновлялся.
        // Единый источник id очереди — лента: id попадает в неё лишь когда
        // карточка уже есть, поэтому `current` всегда резолвится, а гонки нет.
        dispatch(osnovaApi.util.invalidateTags(['Announcement']));
        break;

      case 'econ_bdr_updated':
        dispatch(osnovaApi.util.invalidateTags(['EconBdr']));
        break;

      case 'econ_run_updated':
        // Живое изменение статуса/прогресса прогона (processing → … →
        // completed/failed). Backend шлёт это событие на каждом шаге, поэтому
        // фронт обновляет полосу прогресса и статус в реальном времени без
        // частого polling. Инвалидируем те же теги, что и econ_run_processed:
        //  - { EconRun, run_id } — карточка прогона (RunDetail) перетянет
        //    progress_percent и status;
        //  - 'EconRun' (generic) — оба списка прогонов (сводный реестр и
        //    список модуля) покажут свежий статус/процент.
        dispatch(
          osnovaApi.util.invalidateTags([
            { type: 'EconRun', id: event.run_id },
            'EconRun',
          ]),
        );
        break;

      case 'econ_run_processed':
        // Прогон отчётного периода обработан (строки извлечены, аномалии
        // посчитаны). Инвалидируем карточку прогона и список — экономист
        // видит свежий статус и готовую к HITL таблицу аномалий.
        dispatch(
          osnovaApi.util.invalidateTags([
            { type: 'EconRun', id: event.run_id },
            'EconRun',
          ]),
        );
        break;

      case 'econ_report_composed':
        // Итоговый XLSX-отчёт собран (после HITL-решения accepted) —
        // карточка прогона перетянет статус и покажет кнопку выгрузки.
        dispatch(osnovaApi.util.invalidateTags([{ type: 'EconRun', id: event.run_id }]));
        break;

      case 'department_risk_dispatched':
      case 'department_risks_dispatched':
        // Рассылка рисков по отделам: сотруднику пришла карточка (адресно) либо
        // юристу — сигнал о завершении. Инвалидируем ленту risk-notifications;
        // RiskDispatchPopup сам всплывёт из обновлённой ленты (по seen_at),
        // как UpdatesPopup для «Что нового».
        dispatch(osnovaApi.util.invalidateTags(['RiskNotification']));
        break;
    }
  });
}
